import { api, apiBlob, apiUpload } from '../../../lib/api';
import type {
  PurchaseRequestDetail,
  PurchaseRequestItemOption,
  PurchaseRequestListItem,
  PurchaseRequestListQuery,
  PurchaseRequestRequest,
} from './purchaseRequestTypes';
import type { PagedResult } from '../../administration/users/userTypes';

function toQueryString(query: PurchaseRequestListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  /*
    Each facet is sent whenever the key is PRESENT, empty string included: an empty value is
    the reader saying "none of these", which matches no row. A truthiness check would drop it
    and the server would read the axis as unmentioned — the three-state facet rule.
  */
  if (query.status !== undefined) params.set('status', query.status);
  if (query.stage !== undefined) params.set('stage', query.stage);
  if (query.urgency !== undefined) params.set('urgency', query.urgency);
  if (query.facility !== undefined) params.set('facility', query.facility);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * "We need to buy this" — the register, and the lifecycle around it.
 *
 * **Nothing here approves or rejects.** Those are the approval engine's endpoints, behind
 * `approvals.decide`, and they reach this module through its own handler. A reader who can
 * raise requests is not thereby somebody who can sign them off.
 */
export const purchaseRequestsApi = {
  list: (query: PurchaseRequestListQuery, signal?: AbortSignal) =>
    api<PagedResult<PurchaseRequestListItem>>(`/api/purchase-requests${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<PurchaseRequestDetail>(`/api/purchase-requests/${id}`, { signal }),

  /** The catalog rows a line may name. Carries `create`, because it discloses the catalog. */
  requestableItems: (signal?: AbortSignal) =>
    api<PurchaseRequestItemOption[]>('/api/purchase-requests/requestable-items', { signal }),

  create: (body: PurchaseRequestRequest) =>
    api<PurchaseRequestDetail>('/api/purchase-requests', { method: 'POST', body }),

  /** Corrects a DRAFT. Refused on anything past it. */
  update: (id: string, body: PurchaseRequestRequest) =>
    api<PurchaseRequestDetail>(`/api/purchase-requests/${id}`, { method: 'PUT', body }),

  /** Routes a draft for approval. Its own key, separate from update. */
  submit: (id: string) =>
    api<PurchaseRequestDetail>(`/api/purchase-requests/${id}/submit`, { method: 'POST' }),

  /** Cancels it, closing any approval pending on it. NOT the same as deleting. */
  cancel: (id: string, reason: string) =>
    api<PurchaseRequestDetail>(`/api/purchase-requests/${id}/cancel`, {
      method: 'POST',
      body: { reason },
    }),

  /**
   * Files the Sales Quotation.
   *
   * **`apiUpload`, not `api`** — it is the one XHR path in the product and the only way to a
   * real progress figure, because `fetch` has no upload-progress event. A quotation is up to
   * 10 MB, so a bar that reported nothing would leave the reader unsure it had started.
   */
  attachQuotation: (id: string, file: File, onProgress?: (percent: number) => void) => {
    const form = new FormData();
    form.append('file', file);

    return apiUpload<PurchaseRequestDetail>(`/api/purchase-requests/${id}/quotation`, form, {
      onProgress,
    });
  },

  /**
   * Fetches the filed quotation.
   *
   * **A FETCH, never an `<a href>`.** The endpoint carries `purchase-requests.read`, this SPA
   * authenticates with a bearer token in a header — which no navigation sends — and the API has
   * its own origin, so a bare path would not even reach it. It came back as a `quotationUrl`
   * helper whose own comment said it was unusable, which is a wire contract nothing could
   * consume; `apiBlob` is the one path that inherits the token, the one-shot refresh and the
   * normalized error.
   */
  downloadQuotation: (id: string, signal?: AbortSignal) =>
    apiBlob(`/api/purchase-requests/${id}/quotation`, { signal }),

  removeQuotation: (id: string) =>
    api<PurchaseRequestDetail>(`/api/purchase-requests/${id}/quotation`, { method: 'DELETE' }),

  remove: (id: string) => api<void>(`/api/purchase-requests/${id}`, { method: 'DELETE' }),

  restore: (id: string) => api<void>(`/api/purchase-requests/${id}/restore`, { method: 'POST' }),
};
